import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/axios";
import FormHeader from "@/components/FormHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { FaPlus } from "react-icons/fa6";
import { Loader2 } from "lucide-react";
import { DataSDM, Role, User } from "@/types";
import { MdModeEdit } from "react-icons/md";
import { RiDeleteBack2Line } from "react-icons/ri";
import toast from "react-hot-toast";
import { AxiosError } from "axios";
// import {
//   Select,
//   SelectContent,
//   SelectItem,
//   SelectTrigger,
//   SelectValue,
// } from "@/components/ui/select";
import { listRole } from "@/constants";
import Combobox from "@/components/Combobox";
import InputError from "@/components/InputError";
import { getListSDM } from "@/services/getData";
import { Checkbox } from "@/components/ui/checkbox";

const postData = async (newData: User, jenis_akun: string) => {
  const response = await api.post("/master/user/store", {
    ...newData,
    jenis_akun,
  });
  return response.data;
};

const patchData = async (newData: User, jenis_akun: string) => {
  const response = await api.patch("/master/user/update", {
    ...newData,
    jenis_akun,
  });
  return response.data;
};

const deleteData = async (deletedData: { id: string }) => {
  const response = await api({
    url: "/master/user/delete",
    method: "DELETE",
    data: deletedData,
  });
  return response.data;
};

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE";
  selectedRow?: User | null;
  closeModal: () => void;
}) {
  const userRoles = selectedRow?.jenis_user || [];

  const [form, setForm] = useState<User>({
    id: selectedRow?.id || "",
    nama_lengkap: selectedRow?.nama_lengkap || "",
    email: selectedRow?.email || "",
    password: "",
    password_confirmation: "",
    jenis_user: userRoles || [],
    email_verified_at: "",
  });
  const [selectedJenisAkun, setSelectedJenisAkun] = useState("PRIBADI");
  const [openSdm, setOpenSdm] = useState(false);
  const [searchQuerySdm, setSearchQuerySdm] = useState(
    selectedRow?.nama_lengkap || "",
  );

  const isPost = method === "POST";
  const isPatch = method === "PATCH";
  const isDelete = method === "DELETE";

  const { data: dataSdm, isPending: isPendingSdm } = useQuery({
    queryKey: ["listSDM", searchQuerySdm],
    queryFn: () => getListSDM(undefined, searchQuerySdm, 10),
    select: (data) =>
      data?.map((sdm: DataSDM) => ({
        label: sdm.nama_lengkap,
        value: sdm.email,
      })),
  });

  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: async (variables: { data: User; jenis_akun: string }) => {
      const { data, jenis_akun } = variables;
      if (isPost) return await postData(data, jenis_akun);
      if (isPatch) return await patchData(data, jenis_akun);
      return await deleteData({ id: data.id });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listUser"] });
      closeModal();
      toast.success(data?.message || "Success.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    mutate({ data: form, jenis_akun: selectedJenisAkun });
  };

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data User"
          description={`Apakah anda yakin akan menghapus data user "${selectedRow?.nama_lengkap}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    );

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data User" : "Update Data User"}
      />
      <button
        type="button"
        className="flex justify-center rounded-full border overflow-hidden"
      >
        <div
          className={`flex-1 py-2 px-4 text-sm font-semibold ${
            selectedJenisAkun === "PRIBADI"
              ? "bg-black/90 text-white rounded-full"
              : "hover:bg-secondary"
          }`}
          onClick={() => setSelectedJenisAkun("PRIBADI")}
        >
          Akun Pribadi
        </div>
        <div
          className={`flex-1 py-2 px-4 text-sm font-semibold ${
            selectedJenisAkun === "SSO"
              ? "bg-black/90 text-white rounded-full"
              : "hover:bg-secondary"
          }`}
          onClick={() => {
            setSelectedJenisAkun("SSO");
          }}
        >
          Akun SSO
        </div>
      </button>
      {selectedJenisAkun === "PRIBADI" && (
        <>
          <div>
            <Label>Nama Lengkap</Label>
            <Input
              placeholder="Ketik nama lengkap..."
              value={form.nama_lengkap}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  nama_lengkap: event.target.value,
                }))
              }
            />
          </div>
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              placeholder="Ketik email..."
              value={form.email}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, email: event.target.value }))
              }
            />
          </div>
          {isPost && (
            <>
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  placeholder="Ketik password..."
                  value={form.password}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      password: event.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <Label>Konfirmasi Password</Label>
                <Input
                  type="password"
                  placeholder="Ketik ulang password..."
                  value={form.password_confirmation}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      password_confirmation: event.target.value,
                    }))
                  }
                />
              </div>
            </>
          )}
        </>
      )}

      {selectedJenisAkun === "SSO" && (
        <>
          <div>
            <Label>Nama Pengguna SSO</Label>
            <Combobox
              serverSide
              open={openSdm}
              setOpen={setOpenSdm}
              value={form.email}
              setValue={(newValue) => {
                setForm((prev) => ({
                  ...prev,
                  email: newValue,
                }));
              }}
              options={dataSdm}
              placeholder="Pilih pengguna SSO"
              onChange={(event) => setSearchQuerySdm(event.target.value)}
              searchQueryValue={searchQuerySdm || ""}
              isPending={isPendingSdm}
            />
            <InputError text={error?.response?.data.errors?.email?.join(",")} />
          </div>
        </>
      )}

      <div>
        <Label>Jenis User / Roles</Label>
        <div className="mt-3 grid grid-cols-2 gap-3">
          {listRole.map((role: Role) => {
            const isChecked = form.jenis_user.includes(role);

            return (
              <div key={role} className="flex items-center gap-3">
                <Checkbox
                  checked={isChecked}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({
                      ...prev,
                      jenis_user: checked
                        ? [...prev.jenis_user, role]
                        : prev.jenis_user.filter((r) => r !== role),
                    }))
                  }
                />
                <Label>{role}</Label>
              </div>
            );
          })}
        </div>

        {/* <Select
          value={form.jenis_user}
          onValueChange={(value: Role) => {
            setForm((prev) => ({ ...prev, jenis_user: value }));
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={"Pilih Ref Jalur"}>
              {form.jenis_user &&
                listRole?.find((role) => role === form.jenis_user)}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {listRole?.map((role) => (
              <SelectItem key={role} value={`${role}`}>
                {role}
              </SelectItem>
            ))}
          </SelectContent>
        </Select> */}
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  );
}
